import { PDFDocument, StandardFonts, rgb, degrees } from 'pdf-lib';

/* ============================================================
   Learner Stipend Payslip — A4 portrait, Dignity-Tombstones
   style layout. Shared by the browser (payslip-pdf.js, per-row
   PDF button) and the server (api/payslip-whatsapp-webhook.js),
   so it must not touch the DOM or fetch anything itself.
============================================================ */

const A4  = [595.28, 841.89];   // portrait [width, height]
const M   = 42;                  // left/right margin
const COL = 310;                 // x start of right column

/* ── Public API ─────────────────────────────────────────────── */

/**
 * A stored learner_payslips row in the shape the drawing expects. Stored payslips
 * carry their own gross, UIF and finalise date, so nothing is recalculated here.
 */
export function rowFromStoredPayslip(lp) {
  return {
    student_uuid:        lp.student_uuid,
    full_name:           [lp.first_name, lp.surname].filter(Boolean).join(' '),
    national_id:         lp.national_id,
    employee_number:     lp.employee_number,
    stipend_this_month:  lp.net,
    gross:               lp.gross,
    uif:                 lp.uif,
    days_present:        lp.days_present,
    bank_name:           lp.bank_name,
    bank_account_number: lp.bank_account_number,
    bank_account_type:   lp.bank_account_type,
    branch_code:         lp.branch_code,
    tax_status:          lp.tax_status,
    directive_pct:       lp.directive_pct,
    date_printed:        lp.finalised_at || null,
  };
}

/** Where a learner's stored payslip PDF lives in the private "payslips" bucket. */
export function storedPayslipPath(lp) {
  return `learners/${lp.period_year}-${String(lp.period_month).padStart(2, '0')}/${lp.student_uuid}.pdf`;
}

/**
 * Build one PDF with a page per payslip.
 * @param {Array<{row, year, month, draft?}>} items  row = get_stipend_summary-shaped learner row
 *        or rowFromStoredPayslip(); draft = true stamps DRAFT across the page
 * @param {object} org        org_settings row
 * @param {ArrayBuffer|Uint8Array|null} logoBytes  Londmarx logo PNG, or null for text fallback
 * @returns {Promise<Uint8Array>}
 */
export async function buildLearnerPayslipsPdf(items, org, logoBytes = null) {
  const pdf   = await PDFDocument.create();
  const helv  = await pdf.embedFont(StandardFonts.Helvetica);
  const helvB = await pdf.embedFont(StandardFonts.HelveticaBold);

  let logoImg = null;
  if (logoBytes) {
    try { logoImg = await pdf.embedPng(logoBytes); } catch { /* text fallback */ }
  }

  for (const { row, year, month, draft } of items) {
    const page = pdf.addPage(A4);
    drawLearnerPayslip(page, helv, helvB, logoImg, row, org, year, month);
    if (draft) drawDraftStamp(page, helvB);
  }
  return pdf.save();
}

function drawLearnerPayslip(page, helv, helvB, logoImg, row, org, year, month) {
  const [W, H]      = A4;
  const datePrinted = fmtDateShort(row.date_printed || new Date());

  /* period */
  const periodStart = new Date(year, month - 1, 1);
  const periodEnd   = new Date(year, month, 0);          // last day of month

  /* stipend maths
     NOTE: get_stipend_summary() deducts 1% UIF from every learner's
     stipend_this_month unconditionally (see stipend_config.uif_rate),
     regardless of the student's uif_contributor flag — so the payslip
     must always show/back-calculate the same 1%, not gate on that flag. */
  const uifRate = 0.01;
  const net     = Number(row.stipend_this_month || 0);
  // stored payslips carry their own gross and UIF; otherwise back-calculate: net = gross * (1 - uifRate)
  const gross   = row.gross != null ? Number(row.gross) : Math.round((net / (1 - uifRate)) * 100) / 100;
  const uifAmt  = row.uif   != null ? Number(row.uif)   : Math.round((gross - net) * 100) / 100;

  let y = H - M;   // current y cursor (pdf-lib y=0 is bottom-left)

  /* ── HEADER: logo + title ──────────────────────────────────── */
  if (logoImg) {
    const dim = logoImg.scaleToFit(110, 44);
    page.drawImage(logoImg, { x: M, y: y - dim.height, width: dim.width, height: dim.height });
  } else {
    page.drawText('LONDMARX', { x: M, y: y - 18, size: 14, font: helvB, color: ORANGE });
  }

  page.drawText('Employee Payslip', {
    x: W / 2 - 68, y: y - 10, size: 18, font: helvB, color: rgb(0.1, 0.1, 0.1),
  });

  y -= 52;
  hLine(page, y, M, W - M);

  /* ── EMPLOYER + DATE block ─────────────────────────────────── */
  y -= 4;
  const empRows = [
    ['Employer Name:',   org.company_name             || ''],
    ['Registration No:', org.company_registration     || ''],
    ['Employee Name:',   row.full_name                || ''],
    ['Occupation:',      'Learner / Stipend Recipient'],
  ];
  const empRight = [
    ['Date Printed:',  datePrinted],
    ['',               ''],
    ['ID Number:',     row.national_id    || ''],
    ['Employee ID:',   row.employee_number || ''],
  ];

  for (let i = 0; i < empRows.length; i++) {
    y -= 16;
    labelValue(page, helv, helvB, M,   y, empRows[i][0],  empRows[i][1]);
    labelValue(page, helv, helvB, COL, y, empRight[i][0], empRight[i][1]);
  }

  y -= 8;
  hLine(page, y, M, W - M);

  /* ── PAY PERIOD ─────────────────────────────────────────────── */
  y -= 16;
  labelValue(page, helv, helvB, M,   y, 'Payslip for Period:', fmtDateShort(periodStart));
  page.drawText('to', { x: 230, y, size: 9, font: helv });
  page.drawText(fmtDateShort(periodEnd), { x: 250, y, size: 9, font: helv });
  labelValue(page, helv, helvB, COL, y, 'Date Printed:', datePrinted);

  y -= 16;
  labelValue(page, helv, helvB, M,   y, 'Employer Name:', org.company_name || '');

  /* address lines (left column, under Employer Name) */
  let addrY = y;
  if (org.company_address) {
    const addrLines = org.company_address.split(/,|\n/).map(l => l.trim()).filter(Boolean);
    for (const line of addrLines.slice(0, 3)) {
      addrY -= 14;
      page.drawText(line, { x: M + 115, y: addrY, size: 9, font: helv });
    }
  }

  /* banking block (right column, aligned to Employer Name row) */
  labelValue(page, helv, helvB, COL, y,      'Bank Name:',     row.bank_name           || '—');
  labelValue(page, helv, helvB, COL, y - 14, 'Account:',       row.bank_account_number || '—');
  labelValue(page, helv, helvB, COL, y - 28, 'Account Type:',  row.bank_account_type   || '—');

  /* advance past whichever column (address or bank block) runs deepest */
  y = Math.min(addrY, y - 28) - 12;
  hLine(page, y, M, W - M);

  /* ── EARNINGS ───────────────────────────────────────────────── */
  y -= 18;
  page.drawText('EARNINGS', { x: M, y, size: 11, font: helvB, color: rgb(0.1, 0.1, 0.1) });
  hLine(page, y - 3, M, M + 80, 0.3);

  y -= 18;
  /* column headers */
  colHeader(page, helvB, M,       y, 'Description');
  colHeader(page, helvB, 320,     y, 'Hours');
  colHeader(page, helvB, 390,     y, 'Rate p/h');
  colHeader(page, helvB, W - M - 55, y, 'Amount', true);
  hLine(page, y - 4, M, W - M, 0.4);

  y -= 16;
  page.drawText('Basic Stipend', { x: M, y, size: 9, font: helv });
  page.drawText(rands(gross), { x: W - M - 55, y, size: 9, font: helv });

  y -= 14;
  hLine(page, y, M, W - M, 0.4);
  y -= 14;
  boldRight(page, helvB, helv, W - M, y, 'Total Earnings:', rands(gross));

  y -= 12;
  hLine(page, y, M, W - M);

  /* ── DEDUCTIONS ─────────────────────────────────────────────── */
  y -= 18;
  page.drawText('DEDUCTIONS', { x: M, y, size: 11, font: helvB, color: rgb(0.1, 0.1, 0.1) });
  hLine(page, y - 3, M, M + 90, 0.3);

  y -= 18;
  colHeader(page, helvB, M, y, 'Description');
  colHeader(page, helvB, W - M - 55, y, 'Amount', true);
  hLine(page, y - 4, M, W - M, 0.4);

  y -= 16;
  page.drawText('UIF (1%)', { x: M, y, size: 9, font: helv });
  page.drawText(rands(uifAmt), { x: W - M - 55, y, size: 9, font: helv });

  /* tax-directive row (only if applicable) */
  if (row.tax_status === 'Directive' && row.directive_pct) {
    y -= 14;
    page.drawText(`Tax Directive (${row.directive_pct}%)`, { x: M, y, size: 9, font: helv });
    const dirAmt = Math.round(gross * (row.directive_pct / 100) * 100) / 100;
    page.drawText(rands(dirAmt), { x: W - M - 55, y, size: 9, font: helv });
  }

  y -= 14;
  hLine(page, y, M, W - M, 0.4);
  y -= 14;
  boldRight(page, helvB, helv, W - M, y, 'Total Deductions:', rands(uifAmt));

  y -= 18;
  hLine(page, y, M, W - M, 0.6);
  y -= 14;

  /* Nett Pay */
  page.drawText('Nett Pay:', {
    x: W - M - 130, y, size: 11, font: helvB, color: rgb(0.1, 0.1, 0.1),
  });
  page.drawText(rands(net), {
    x: W - M - 55, y, size: 11, font: helvB, color: rgb(0.1, 0.1, 0.1),
  });

}

/* ── Drawing helpers ────────────────────────────────────────── */

/** Diagonal DRAFT across a preview of a payslip that is not final yet. */
function drawDraftStamp(page, helvB) {
  const [W, H] = A4;
  const text = 'DRAFT', size = 120;
  const w = helvB.widthOfTextAtSize(text, size);
  page.drawText(text, {
    x: W / 2 - (w / 2) * Math.cos(Math.PI / 4), y: H / 2 - (w / 2) * Math.sin(Math.PI / 4),
    size, font: helvB, color: rgb(0.85, 0.85, 0.85), opacity: 0.45, rotate: degrees(45),
  });
}

const ORANGE = rgb(0.85, 0.16, 0.07);
const GREY   = rgb(0.4, 0.4, 0.4);

function hLine(page, y, x1, x2, width = 0.5, color = rgb(0.7, 0.7, 0.7)) {
  page.drawLine({ start: { x: x1, y }, end: { x: x2, y }, thickness: width, color });
}

function labelValue(page, helv, helvB, x, y, label, value) {
  if (label) page.drawText(label, { x, y, size: 8.5, font: helvB, color: GREY });
  const vx = x + (label ? Math.max(80, label.length * 5.5) : 0);
  page.drawText(String(value ?? ''), { x: Math.min(vx, x + 120), y, size: 9, font: helv });
}

function colHeader(page, helvB, x, y, text, rightAlign = false) {
  page.drawText(text, {
    x: rightAlign ? x - helvB.widthOfTextAtSize(text, 8) : x,
    y, size: 8, font: helvB, color: GREY,
  });
}

function boldRight(page, helvB, helv, rightEdge, y, label, value) {
  page.drawText(label, { x: rightEdge - 185, y, size: 9.5, font: helvB });
  page.drawText(value, { x: rightEdge - 60,  y, size: 9.5, font: helv });
}

function rands(n) {
  return `R ${Number(n || 0).toFixed(2)}`;
}

function fmtDateShort(d) {
  if (!d) return '';
  const dd = new Date(d);
  return `${dd.getDate()}/${dd.getMonth() + 1}/${dd.getFullYear()}`;
}
